import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ProductName } from 'situ-warpapi-client';
import { RecipeEventSummary, EnvironmentName, SituAnalyticsWebApiClient, UpdateRecipeEventSummaryStatusRequest } from 'situanalytics-webapi-client';
import { ProductNameService, ConfirmDialogService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'recipe-event-summary-card',
  templateUrl: './recipe-event-summary-card.component.html',
  styleUrls: ['./recipe-event-summary-card.component.less']
})
export class RecipeEventSummaryCardComponent {
  @Input() recipeEventSummary: RecipeEventSummary | undefined;
  @Input() searchTerm: string | undefined;
  @Input() showRenderer: boolean = false;
  @Output() statusUpdated = new EventEmitter<void>();

  productName = ProductName;
  environmentName = EnvironmentName;
  updating = false;

  constructor(
    private productNameService: ProductNameService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private snackBar: MatSnackBar,
    private confirmDialog: ConfirmDialogService
  ) {}

  getStatusIconAndClass(recipeEventSummary: RecipeEventSummary) {
    // in progress
    const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
      && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
    if (isInProgress) {
      return { icon: "pending", class: "text-info", description: "In Progress" };
    }

    // completed
    if (!!recipeEventSummary.dateCompleted) {
      return { icon: "check_circle", class: "text-success", description: "Completed" };
    }

    // failed
    if (!!recipeEventSummary.dateFailed) {
      return { icon: "error", class: "text-danger", description: "Failed" };
    }

    // cancelled
    if (!!recipeEventSummary.dateCancelled) {
      return { icon: "block", class: "text-muted", description: "Cancelled" };
    }

    // queued
    if (!!recipeEventSummary.dateQueued) {
      return { icon: "pending", class: "text-muted", description: "Queued" };
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  canUpdateStatus(): boolean {
    if (!this.recipeEventSummary) return false;
    
    // Can only update if the recipe is in progress or queued
    const isInProgress = !this.recipeEventSummary.dateCancelled && !this.recipeEventSummary.dateFailed
      && !this.recipeEventSummary.dateCompleted && !!this.recipeEventSummary.dateStarted;
    const isQueued = !this.recipeEventSummary.dateCancelled && !this.recipeEventSummary.dateFailed
      && !this.recipeEventSummary.dateCompleted && !this.recipeEventSummary.dateStarted && !!this.recipeEventSummary.dateQueued;
    
    return isInProgress || isQueued;
  }

  updateStatus(markAsFailed: boolean, markAsCancelled: boolean) {
    if (!this.recipeEventSummary?.id || this.updating) return;

    const action = markAsFailed ? 'fail' : 'cancel';
    const actionColour = markAsFailed ? 'warn' : 'basic';
    
    this.confirmDialog.open({
      title: `Confirm ${action}`,
      message: `Are you sure you want to ${action} this recipe event summary?`,
      confirmText: 'Yes',
      cancelText: 'No',
      actionIcon: markAsFailed ? 'error' : 'cancel',
      actionColour: actionColour
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performStatusUpdate(markAsFailed, markAsCancelled);
      }
    });
  }

  private performStatusUpdate(markAsFailed: boolean, markAsCancelled: boolean) {
    if (!this.recipeEventSummary?.id) return;

    this.updating = true;
    const request = new UpdateRecipeEventSummaryStatusRequest();
    request.recipeEventSummaryId = this.recipeEventSummary.id;
    request.markAsFailed = markAsFailed;
    request.markAsCancelled = markAsCancelled;
    request.reason = markAsFailed ? 'Marked as failed by user' : 'Cancelled by user';

    this.situAnalyticsClient.updateRecipeEventSummaryStatus(request)
      .then(response => {
        if (response.success) {
          const action = markAsFailed ? 'failed' : 'cancelled';
          this.showMessage(`Recipe event summary marked as ${action}`);
          this.statusUpdated.emit();
        } else {
          this.showMessage('Failed to update status');
        }
      })
      .catch(error => {
        console.error('Error updating status:', error);
        this.showMessage('Error updating status');
      })
      .finally(() => {
        this.updating = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}